// Export zoznamov do Excelu.
//
// Predtym sa vsade generovalo CSV rucne cez spajanie retazcov. V Exceli sa to
// rozsypalo: stredniky verzus ciarky, diakritika, cisla brane ako text a
// vzorec HYPERLINK, ktory Excel nemusel prijat. Tu je jeden format pre cely
// admin: tucna hlavicka, zmrazeny prvy riadok, automaticky filter, rozumne
// sirky, skutocne hyperlinky, datumy ako datumy a cisla ako cisla.
//
// Kniznica exceljs sa nacita az pri kliknuti na export, aby nezvacsovala
// hlavny balik administracie.

const FARBA_HLAVICKY = "FF5F63F2"; // rovnaka modra ako tlacidla v admine
const FARBA_PRUHU = "FFF4F5F7";
const FARBA_RAMU = "FFE3E6EF";

const dnesnyDatum = () => {
  const d = new Date();
  const dva = (c) => String(c).padStart(2, "0");
  return `${d.getFullYear()}-${dva(d.getMonth() + 1)}-${dva(d.getDate())}`;
};

const naDatum = (hodnota) => {
  if (!hodnota) return null;
  const d = hodnota instanceof Date ? hodnota : new Date(hodnota);
  return Number.isNaN(d.getTime()) ? null : d;
};

const naCislo = (hodnota) => {
  if (hodnota === null || hodnota === undefined || hodnota === "") return null;
  const c = Number(String(hodnota).replace(",", "."));
  return Number.isNaN(c) ? null : c;
};

/**
 * @param {Object} moznosti
 * @param {string} moznosti.nazovSuboru bez pripony
 * @param {string} moznosti.nazovHarku nazov listu v zosite
 * @param {Array} moznosti.stlpce [{ kluc, popis, sirka, typ }]
 *        typ: "text" (predvolene) | "cislo" | "penize" | "datum" | "odkaz"
 *        pri type "odkaz" je hodnota { text, url }
 * @param {Array} moznosti.riadky data, kluce zodpovedaju stlpcom
 * @param {Object} [moznosti.info] doplnkove udaje na druhy hárok (napr. filtre)
 */
export const exportujDoExcelu = async ({
  nazovSuboru,
  nazovHarku,
  stlpce,
  riadky,
  info,
}) => {
  const ExcelJS = (await import("exceljs")).default || (await import("exceljs"));
  const zosit = new ExcelJS.Workbook();
  zosit.creator = "Pneusystem";
  zosit.created = new Date();

  const harok = zosit.addWorksheet(nazovHarku || "Zoznam", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  harok.columns = stlpce.map((s) => ({
    header: s.popis,
    key: s.kluc,
    width: s.sirka || Math.max(12, String(s.popis || "").length + 4),
  }));

  const hlavicka = harok.getRow(1);
  hlavicka.height = 22;
  hlavicka.eachCell((bunka) => {
    bunka.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
    bunka.fill = { type: "pattern", pattern: "solid", fgColor: { argb: FARBA_HLAVICKY } };
    bunka.alignment = { vertical: "middle", horizontal: "left" };
  });

  (riadky || []).forEach((zaznam, poradie) => {
    const riadok = harok.addRow({});
    stlpce.forEach((s, index) => {
      const bunka = riadok.getCell(index + 1);
      const hodnota = zaznam[s.kluc];

      if (s.typ === "odkaz") {
        const text = hodnota?.text ?? "";
        const url = hodnota?.url;
        if (url) {
          bunka.value = { text: String(text), hyperlink: String(url) };
          bunka.font = { color: { argb: "FF1B55E2" }, underline: true };
        } else {
          bunka.value = String(text);
        }
      } else if (s.typ === "datum") {
        const d = naDatum(hodnota);
        bunka.value = d;
        bunka.numFmt = "d.m.yyyy";
      } else if (s.typ === "cislo") {
        bunka.value = naCislo(hodnota);
        bunka.numFmt = "#,##0";
      } else if (s.typ === "penize") {
        bunka.value = naCislo(hodnota);
        bunka.numFmt = '#,##0.00 "€"';
      } else {
        bunka.value =
          hodnota === null || hodnota === undefined ? "" : String(hodnota);
      }

      bunka.alignment = { vertical: "middle", wrapText: false };
      bunka.border = {
        bottom: { style: "thin", color: { argb: FARBA_RAMU } },
      };
      if (poradie % 2 === 1) {
        bunka.fill = { type: "pattern", pattern: "solid", fgColor: { argb: FARBA_PRUHU } };
      }
    });
  });

  // Automaticky filter nad hlavickou, nech sa da v Exceli hned triedit.
  if (stlpce.length > 0) {
    harok.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: Math.max(1, (riadky || []).length + 1), column: stlpce.length },
    };
  }

  // Druhy hárok s tym, kedy a s akymi filtrami sa export robil. Do prveho
  // hárku to nedavame, aby nad hlavickou nic neprekazalo pri filtrovani.
  const harokInfo = zosit.addWorksheet("Info");
  harokInfo.columns = [
    { header: "Údaj", key: "udaj", width: 28 },
    { header: "Hodnota", key: "hodnota", width: 60 },
  ];
  harokInfo.getRow(1).eachCell((bunka) => {
    bunka.font = { bold: true, color: { argb: "FFFFFFFF" } };
    bunka.fill = { type: "pattern", pattern: "solid", fgColor: { argb: FARBA_HLAVICKY } };
  });
  harokInfo.addRow({ udaj: "Zoznam", hodnota: nazovHarku || "" });
  harokInfo.addRow({ udaj: "Vytvorené", hodnota: new Date().toLocaleString("sk-SK") });
  harokInfo.addRow({ udaj: "Počet záznamov", hodnota: (riadky || []).length });
  Object.entries(info || {}).forEach(([udaj, hodnota]) => {
    if (hodnota !== undefined && hodnota !== null && String(hodnota) !== "") {
      harokInfo.addRow({ udaj, hodnota: String(hodnota) });
    }
  });

  const data = await zosit.xlsx.writeBuffer();
  const subor = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const odkaz = document.createElement("a");
  odkaz.href = URL.createObjectURL(subor);
  odkaz.download = `${nazovSuboru}-${dnesnyDatum()}.xlsx`;
  document.body.appendChild(odkaz);
  odkaz.click();
  document.body.removeChild(odkaz);
  setTimeout(() => URL.revokeObjectURL(odkaz.href), 1000);
};

/** Adresa zaznamu v admine, aby sa z Excelu dalo kliknut priamo na neho. */
export const odkazNaAdmin = (cesta) => `${window.location.origin}${cesta}`;
